import React from "react";
import { SalonReport } from "../../../types/reports";
import { ReportV2Definition } from "../../../types/reportsV2";
import MobileReportCanvas from "../mobile/MobileReportCanvas";
import { CanvasHeaderProps } from "./canvasTypes";
import DesktopReportCanvas from "./DesktopReportCanvas";
import { useReportCanvas } from "./hooks/useReportCanvas";
import ReportPrintView from "./print/ReportPrintView";

export type { CanvasHeaderProps } from "./canvasTypes";

export interface ReportCanvasProps {
  salonId: string;
  report: SalonReport;
  definition: ReportV2Definition;
  /** Name / description / menu wiring for the header. */
  header: CanvasHeaderProps;
  /** Put the saved report back into the shell's list. */
  onReportSaved: (report: SalonReport) => void;
  /** Shell Duplicate: copies the report with the view on screen (the unsaved
   *  draft, read through the registered draft source) and opens the copy. */
  onDuplicate: () => void;
  /** Sidebar unsaved dot. */
  onDirtyChange: (dirty: boolean) => void;
  /** Phone layout (the app's mobile breakpoint). */
  mobile?: boolean;
}

/**
 * A v2 report (the Daily Sales Report). Owns the one `useReportCanvas`
 * model — draft vs saved view with the navigation guard, the run, the table
 * model, export and print — and renders it with the desktop or the phone
 * layout. Because the model lives here, crossing the mobile breakpoint
 * (tablet rotation, split screen, zoom) swaps only the layout: an unsaved
 * view, its guard and the current result all survive.
 */
export default function ReportCanvas({
  salonId,
  report,
  definition,
  header,
  onReportSaved,
  onDuplicate,
  onDirtyChange,
  mobile = false,
}: ReportCanvasProps) {
  const canvas = useReportCanvas({
    salonId,
    report,
    definition,
    name: header.name,
    description: header.description,
    onReportSaved,
    onDirtyChange,
  });
  const View = mobile ? MobileReportCanvas : DesktopReportCanvas;
  return (
    <>
      <View
        canvas={canvas}
        definition={definition}
        header={header}
        onDuplicate={onDuplicate}
      />
      {canvas.printProps && <ReportPrintView {...canvas.printProps} />}
    </>
  );
}
